/**
 * objects.js — Placeable object definitions
 * Each object is a token of type 'object' with its own icon, default HP and size.
 */

export const OBJECT_TYPES = [
  // ── Containers & Furniture ─────────────────────────────────────────────────
  { id: 'barrel',      label: 'Barrel',      icon: '🛢',  hp: 5,  size: 1, color: '#5a3a18' },
  { id: 'crate',       label: 'Crate',       icon: '📦',  hp: 8,  size: 1, color: '#6a4a20' },
  { id: 'chest',       label: 'Chest',       icon: '📫',  hp: 10, size: 1, color: '#7a4a10' },
  { id: 'table',       label: 'Table',       icon: '🍽',  hp: 10, size: 1, color: '#5a3a14' },
  { id: 'table_long',  label: 'Long Table',  icon: '🍽',  hp: 15, size: 1, sizeW: 1, sizeH: 3, color: '#5a3a14' },
  { id: 'chair',       label: 'Chair',       icon: '🪑',  hp: 5,  size: 1, color: '#4a3010' },
  { id: 'bookshelf',   label: 'Bookshelf',   icon: '📚',  hp: 12, size: 1, color: '#4a3018' },
  { id: 'bed',         label: 'Bed',         icon: '🛏',  hp: 8,  size: 2, color: '#5a4a38' },
  { id: 'throne',      label: 'Throne',      icon: '👑',  hp: 20, size: 1, color: '#6a5020' },
  // ── Structural ─────────────────────────────────────────────────────────────
  { id: 'boulder',     label: 'Boulder',     icon: '🪨',  hp: 30, size: 1, color: '#4a4a4a' },
  { id: 'pillar',      label: 'Pillar',      icon: '🏛',  hp: 40, size: 1, color: '#6a6a7a' },
  { id: 'statue',      label: 'Statue',      icon: '🗿',  hp: 30, size: 1, color: '#5a5a6a' },
  { id: 'portcullis',  label: 'Portcullis',  icon: '⛓',  hp: 50, size: 1, color: '#3a3a4a' },
  { id: 'stairs',      label: 'Stairs',      icon: '🪜',  hp: 99, size: 1, color: '#5a5a6a', passable: true },
  { id: 'stairs_long', label: 'Long Stairs', icon: '🪜',  hp: 99, size: 1, sizeW: 1, sizeH: 2, color: '#5a5a6a', passable: true },
  { id: 'spiral_stairs',label:'Spiral Stairs',icon: '🌀',  hp: 99, size: 1, color: '#5a5a6a', passable: true },
  { id: 'altar',       label: 'Altar',       icon: '🏺',  hp: 30, size: 2, color: '#5a4a3a' },
  { id: 'rubble',      label: 'Rubble',      icon: '💥',  hp: 1,  size: 1, color: '#5a5050', passable: true },
  // ── Outdoors ───────────────────────────────────────────────────────────────
  { id: 'tree',        label: 'Tree',        icon: '🌲',  hp: 20, size: 1, color: '#2a4a1a' },
  { id: 'large_tree',  label: 'Large Tree',  icon: '🌳',  hp: 40, size: 2, color: '#1a3a0a' },
  { id: 'well',        label: 'Well',        icon: '🪣',  hp: 20, size: 1, color: '#3a5a3a' },
  { id: 'fountain',    label: 'Fountain',    icon: '⛲',  hp: 25, size: 2, color: '#3a5a6a' },
  { id: 'cart',        label: 'Cart',        icon: '🛒',  hp: 15, size: 1, color: '#5a4a1a' },
  { id: 'wagon',       label: 'Wagon',       icon: '🪠',  hp: 20, size: 2, color: '#5a3a14' },
  { id: 'gravestone',  label: 'Gravestone',  icon: '🪦',  hp: 15, size: 1, color: '#4a4a54' },
  // ── Light & Fire ───────────────────────────────────────────────────────────
  { id: 'campfire',    label: 'Campfire',    icon: '🔥',  hp: 5,  size: 1, color: '#2a1a0a' },
  { id: 'torch',       label: 'Torch',       icon: '🕯',  hp: 3,  size: 1, color: '#3a2a0a' },
  { id: 'brazier',     label: 'Brazier',     icon: '🔆',  hp: 8,  size: 1, color: '#4a3010' },
  // ── Dungeon ────────────────────────────────────────────────────────────────
  { id: 'cage',        label: 'Cage',        icon: '🔒',  hp: 25, size: 1, color: '#3a3a4a' },
  { id: 'cauldron',    label: 'Cauldron',    icon: '🫕',  hp: 12, size: 1, color: '#2a2a3a' },
  { id: 'forge',       label: 'Forge',       icon: '⚒',  hp: 30, size: 2, color: '#3a2a1a' },
  { id: 'trap',        label: 'Trap',        icon: '⚙️', hp: 5,  size: 1, color: '#4a3a2a' },
];

export const OBJECT_MAP = Object.fromEntries(OBJECT_TYPES.map((o) => [o.id, o]));

/** Return { w, h } cell footprint accounting for rotation (0/90/180/270). */
export function objectFootprint(token) {
  const def  = OBJECT_MAP[token.objectType];
  const baseW = token.sizeW || def?.sizeW || token.size || 1;
  const baseH = token.sizeH || def?.sizeH || token.size || 1;
  const rot90 = (token.rotation === 90 || token.rotation === 270);
  return { w: rot90 ? baseH : baseW, h: rot90 ? baseW : baseH, baseW, baseH };
}
